import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from '../glossary.module.scss';
import formClasses from '../../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import { GlossaryLanguage, GlossaryTermItem, GlossaryTermCreatePayload } from '../../../services/api.service';
import GlossaryChipsInput from './glossary-chips-input.component';

const cn = createCn('glossary', classes);

/** `null` = термин применяется для всех языков — соответствует `language: null` на бэке. */
type LanguageOption = GlossaryLanguage | 'all';

interface GlossaryFormProps {
  /** null — создание нового термина, иначе — редактирование переданного. */
  term: GlossaryTermItem | null;
  saving: boolean;
  onSubmit: (payload: GlossaryTermCreatePayload) => void;
  onCancel: () => void;
}

const GlossaryForm = ({ term, saving, onSubmit, onCancel }: GlossaryFormProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(term?.term ?? '');
  const [description, setDescription] = useState(term?.description ?? '');
  const [language, setLanguage] = useState<LanguageOption>(term?.language ?? 'all');
  const [aliases, setAliases] = useState<string[]>(term?.aliases ?? []);
  const [isActive, setIsActive] = useState(term?.is_active ?? true);
  const [validationError, setValidationError] = useState('');

  const isEditing = Boolean(term);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = value.trim();
    if (!trimmed) {
      setValidationError(t('glossary.form-error-term-required'));
      return;
    }

    setValidationError('');
    onSubmit({
      term: trimmed,
      description: description.trim() || undefined,
      aliases,
      language: language === 'all' ? null : language,
      is_active: isActive,
    });
  };

  return (
    <form className={cn('form')} onSubmit={handleSubmit}>
      <div className={cn('form-title')}>
        {isEditing ? t('glossary.form-title-edit', { term: term?.term }) : t('glossary.form-title-create')}
      </div>

      <div className={cn('form-row')}>
        <div className={formClasses['form-group']}>
          <label>{t('glossary.form-term-label')}</label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('glossary.form-term-placeholder') as string}
            disabled={saving}
            autoFocus
          />
        </div>

        <div className={formClasses['form-group']}>
          <label>{t('glossary.form-language-label')}</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value as LanguageOption)} disabled={saving}>
            <option value="all">{t('glossary.filter-language-all')}</option>
            <option value="ru">Русский</option>
            <option value="kk">Қазақша</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <div className={formClasses['form-group']}>
        <label>{t('glossary.form-description-label')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('glossary.form-description-placeholder') as string}
          disabled={saving}
        />
      </div>

      <div className={formClasses['form-group']}>
        <label>{t('glossary.form-aliases-label')}</label>
        <GlossaryChipsInput
          values={aliases}
          onChange={setAliases}
          disabled={saving}
          placeholder={t('glossary.form-aliases-placeholder') as string}
          aria-label={t('glossary.form-aliases-label') as string}
        />
        <div className={cn('form-hint')}>{t('glossary.form-aliases-hint')}</div>
      </div>

      <div className={cn('form-active-row')}>
        <label className={cn('switch')}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={saving}
            aria-label={t('glossary.form-active-label') as string}
          />
          <span className={cn('switch-track')} />
          <span className={cn('switch-thumb')} />
        </label>
        <span>{t('glossary.form-active-label')}</span>
      </div>

      {validationError && <div className={cn('form-error')}>{validationError}</div>}

      <div className={cn('form-actions')}>
        <button type="submit" className={cn('save-btn')} disabled={saving}>
          {saving ? t('glossary.form-saving') : isEditing ? t('glossary.form-save-edit') : t('glossary.form-save-create')}
        </button>
        <button type="button" className={cn('cancel-btn')} onClick={onCancel} disabled={saving}>
          {t('glossary.form-cancel')}
        </button>
      </div>
    </form>
  );
};

export default GlossaryForm;
